import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { inputCls, Field, Card, money, todayISO } from "./booksShared";

const EMPTY = {
  date: todayISO(), customer: "", product: "", amount: "", tax_collected: "",
  city: "", county: "", payment_method: "", notes: "",
};

export const BooksSales = () => {
  const [sales, setSales] = useState([]);
  const [cityRates, setCityRates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/books/sales").then((r) => setSales(r.data)).catch(() => toast.error("Could not load sales")).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/admin/books/settings").then((r) => {
      setCityRates(r.data.city_rates || []);
      setForm((f) => ({ ...f, city: r.data.default_city || "", county: r.data.default_county || "" }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cityRate = cityRates.find((c) => c.city === form.city)?.rate || 0;

  // Prices include sales tax across the shop, so when an amount and a rated
  // city are entered, suggest the tax portion inside that amount (editable).
  useEffect(() => {
    const amt = parseFloat(form.amount);
    if (amt > 0 && cityRate > 0) {
      const t = (Math.round(((amt * cityRate) / (100 + cityRate)) * 100) / 100).toFixed(2);
      setForm((f) => (f.tax_collected === t ? f : { ...f, tax_collected: t }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, form.city, cityRate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.date || !form.amount) {
      toast.error("Date and sales amount are required.");
      return;
    }
    setBusy(true);
    const payload = { ...form, amount: parseFloat(form.amount) || 0, tax_collected: parseFloat(form.tax_collected) || 0 };
    try {
      if (editing) {
        await api.put(`/admin/books/sales/${editing.id}`, payload);
        toast.success("Sale updated — the Orders page matches");
      } else {
        await api.post("/admin/books/sales", payload);
        toast.success("Sale saved — it's on the Orders page too");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/books/sales/${id}`);
      toast.success("Sale deleted — also removed from Orders");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="books-sales">
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => { setEditing(null); setForm({ ...EMPTY, city: form.city, county: form.county }); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-terra hover:bg-terra-hover text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
          data-testid="books-add-sale-button"
        >
          <Plus className="w-4 h-4" /> Add Sale
        </button>
      </div>

      {showForm && (
        <Card title={editing ? "Edit Sale" : "New Sale"} testid="books-sale-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Date *"><input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="sale-date" /></Field>
            <Field label="Customer / Order"><input value={form.customer} onChange={set("customer")} className={inputCls} data-testid="sale-customer" /></Field>
            <Field label="Product or Service"><input value={form.product} onChange={set("product")} className={inputCls} data-testid="sale-product" /></Field>
            <Field label="Sale amount received ($) *"><input type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} className={inputCls} data-testid="sale-amount" /></Field>
            <Field label="City">
              <select value={form.city} onChange={set("city")} className={inputCls} data-testid="sale-city">
                <option value="">Choose city…</option>
                {cityRates.map((c) => (
                  <option key={c.city} value={c.city}>{c.city}</option>
                ))}
                {form.city && !cityRates.some((c) => c.city === form.city) && (
                  <option value={form.city}>{form.city}</option>
                )}
              </select>
            </Field>
            <Field label="Tax portion in that amount ($)">
              <input type="number" step="0.01" min="0" value={form.tax_collected} onChange={set("tax_collected")} className={inputCls} data-testid="sale-tax" />
              <p className="text-[11px] text-ink/45 mt-1" data-testid="sale-tax-hint">
                {cityRate > 0
                  ? `Auto-filled at the ${form.city} rate of ${cityRate}% — edit if this sale was tax-free.`
                  : "Pick a city above to auto-fill, or type the tax yourself (0 if tax-free)."}
              </p>
            </Field>
            <Field label="Payment Method"><input value={form.payment_method} onChange={set("payment_method")} placeholder="CashApp, Venmo, cash…" className={inputCls} data-testid="sale-method" /></Field>
            <Field label="County"><input value={form.county} onChange={set("county")} className={inputCls} data-testid="sale-county" /></Field>
            <Field label="Notes"><input value={form.notes} onChange={set("notes")} className={inputCls} data-testid="sale-notes" /></Field>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy} className="bg-dill hover:bg-terra disabled:opacity-50 text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors flex items-center gap-2" data-testid="sale-save-button">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save Changes" : "Save Sale"}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-sm text-ink/50 hover:text-terra transition-colors" data-testid="sale-cancel-button">Cancel</button>
          </div>
        </Card>
      )}

      <Card title={`Sales (${sales.length})`} testid="books-sales-table">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-dill" /></div>
        ) : sales.length === 0 ? (
          <p className="text-sm text-ink/50 italic">No sales yet — add one above or import your paid shop orders.</p>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-ink/45 text-left border-b border-parchborder">
                  <th className="py-2 pr-4">Date</th><th className="pr-4">Customer</th><th className="pr-4">Product</th>
                  <th className="pr-4 text-right">Amount</th><th className="pr-4 text-right">Tax</th><th className="pr-4">Method</th><th className="pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.id} className="border-b border-parchborder/60" data-testid={`sale-row-${s.id}`}>
                    <td className="py-2.5 pr-4 font-mono text-xs whitespace-nowrap">{s.date}</td>
                    <td className="pr-4">{s.customer || "—"}</td>
                    <td className="pr-4 max-w-48 truncate">{s.product || "—"}</td>
                    <td className="pr-4 text-right font-semibold">{money(s.amount)}</td>
                    <td className="pr-4 text-right text-ink/60">{money(s.tax_collected)}</td>
                    <td className="pr-4 text-ink/60 capitalize">{s.payment_method || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(s); setForm({ ...EMPTY, ...s }); setShowForm(true); }} className="p-1.5 text-ink/40 hover:text-dill transition-colors" aria-label="Edit sale" data-testid={`sale-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(s.id)} className="p-1.5 text-ink/40 hover:text-terra transition-colors" aria-label="Delete sale" data-testid={`sale-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
